import React, { useState } from 'react';
import Link from 'next/link';
import type { TopSearchItemWithDerived, Condition } from '@util/insights/topSearchesNov2025';
import { PriceRange } from './PriceRange';
import { ConditionAdjuster } from './ConditionAdjuster';

interface DataTableDesktopProps {
  items: TopSearchItemWithDerived[];
  onCompsClick: (item: TopSearchItemWithDerived) => void;
  onListClick: (item: TopSearchItemWithDerived, suggestedAsk: number) => void;
  onConditionAdjust: (skuId: string, condition: Condition) => void;
  onNoteExpand: (skuId: string) => void;
}

export const DataTableDesktop: React.FC<DataTableDesktopProps> = ({
  items,
  onCompsClick,
  onListClick,
  onConditionAdjust,
  onNoteExpand,
}) => {
  const [suggestedAsks, setSuggestedAsks] = useState<Record<string, number>>({});

  const handleConditionChange = (skuId: string, condition: Condition, suggestedPrice: number) => {
    setSuggestedAsks({ ...suggestedAsks, [skuId]: suggestedPrice });
    onConditionAdjust(skuId, condition);
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      Camera: 'bg-blue-600 text-white',
      Lens: 'bg-green-600 text-white',
      Drone: 'bg-purple-600 text-white',
      Accessory: 'bg-orange-600 text-white',
      Lighting: 'bg-yellow-600 text-white',
      Video: 'bg-red-600 text-white',
    };
    return colors[category] || 'bg-gray-600 text-white';
  };

  if (items.length === 0) {
    return (
      <div className="hidden md:block text-center py-12 text-gray-500">
        No items match your filters.
      </div>
    );
  }

  return (
    <div className="hidden md:block overflow-x-auto">
      <table className="w-full border-collapse">
        <thead className="bg-gray-50 sticky top-0 z-10">
          <tr className="border-b border-gray-200">
            <th className="px-4 py-3 text-left text-base font-semibold text-gray-700 uppercase tracking-wider">
              Category
            </th>
            <th className="px-4 py-3 text-left text-base font-semibold text-gray-700 uppercase tracking-wider">
              Brand
            </th>
            <th className="px-4 py-3 text-left text-base font-semibold text-gray-700 uppercase tracking-wider">
              Model
            </th>
            <th className="px-4 py-3 text-left text-base font-semibold text-gray-700 uppercase tracking-wider">
              Price Range
            </th>
            <th className="px-4 py-3 text-left text-base font-semibold text-gray-700 uppercase tracking-wider">
              Condition
            </th>
            <th className="px-4 py-3 text-left text-base font-semibold text-gray-700 uppercase tracking-wider">
              Notes
            </th>
            <th className="px-4 py-3 text-left text-base font-semibold text-gray-700 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-100">
          {items.map((item, index) => (
            <tr
              key={item.sku_id}
              className={`hover:bg-gray-50 transition-colors ${
                index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
              }`}
            >
              {/* Category */}
              <td className="px-4 py-3 whitespace-nowrap">
                <span
                  className={`inline-block px-2 py-1 text-xs font-medium rounded ${getCategoryColor(
                    item.category
                  )}`}
                >
                  {item.category}
                </span>
              </td>

              {/* Brand */}
              <td className="px-4 py-3 font-bold text-gray-900">
                {item.brand}
              </td>

              {/* Model */}
              <td className="px-4 py-3 text-gray-700">{item.model}</td>

              {/* Price Range */}
              <td className="px-4 py-3">
                <PriceRange
                  low={item.low_price}
                  high={item.high_price}
                  median={item.median_price}
                />
              </td>

              {/* Condition Adjuster */}
              <td className="px-4 py-3">
                <ConditionAdjuster
                  lowPrice={item.low_price}
                  highPrice={item.high_price}
                  skuId={item.sku_id}
                  onConditionChange={(condition, suggestedPrice) =>
                    handleConditionChange(item.sku_id, condition, suggestedPrice)
                  }
                />
              </td>

              {/* Notes */}
              <td className="px-4 py-3">
                <p className="text-xl text-gray-600">
                  {item.note_seller}
                </p>
              </td>

              {/* Actions */}
              <td className="px-4 py-3 whitespace-nowrap">
                <div className="flex flex-col gap-2">
                  <Link
                    href={`/sell?brand=${encodeURIComponent(
                      item.brand
                    )}&model=${encodeURIComponent(
                      item.model
                    )}&category=${encodeURIComponent(
                      item.category
                    )}&ask=${suggestedAsks[item.sku_id] || item.median_price}`}
                    onClick={() =>
                      onListClick(item, suggestedAsks[item.sku_id] || item.median_price)
                    }
                    className="px-3 py-1.5 bg-brand-orange text-white text-sm font-medium rounded hover:bg-orange-600 transition-colors text-center"
                  >
                    List this item
                  </Link>
                  <button
                    onClick={() => onCompsClick(item)}
                    className="px-3 py-1.5 border border-gray-300 text-gray-700 text-sm font-medium rounded hover:bg-gray-50 transition-colors"
                  >
                    See comps
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
